import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from '../../../components/hooks/useForm';
import ClearIcon from '@mui/icons-material/Clear';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import SearchIcon from '@mui/icons-material/Search';
import { MenuItem } from '@mui/material';
import { FormControl, FormGroup, Grid, InputLabel, Select, Stack, TextField, Typography } from '@mui/material';
import { AntSwitch } from './components/AntSwitch';
import toast from 'react-hot-toast';
import ErrorDialog from '../../../components/ErrorDialog';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ScreenSearchDesktopIcon from '@mui/icons-material/ScreenSearchDesktop';
import TaskIcon from '@mui/icons-material/Task';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import Box from '@mui/material/Box';
import { StyledSpeedDial } from './components/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { GET_TIPO_PROFESIONALES } from '../../../data/graphQL/queries/AdminRoles/getTipoProfesionales';
import { GET_FUNCIONES } from '../../../data/graphQL/queries/AdminRoles/getFunciones';
import { CREATE_ROL, adaptListaConsultaRolInput } from '../../../data/graphQL/mutations/AdminRoles/createRol';
import { UPDATE_ROL } from '../../../data/graphQL/mutations/AdminRoles/updateRol';
import RoleRelationSection from './relations/RoleRelationSection';
import RoleRelationDialog from './relations/RoleRelationDialog';
import useRoleApps from './hooks/useRoleApps';
import useRoleNotifications from './hooks/useRoleNotifications';
import useRoleKpis from './hooks/useRoleKpis';
import useRoleIncompatibleRoles from './hooks/useRoleIncompatibleRoles';
import useRoleConsultas from './hooks/useRoleConsultas';
import './role.scss';

export default function Role({ setChildrenAction }) {
  const { state } = useLocation();
  const { role, edit } = state;
  const navigate = useNavigate();

  const [datosTipoProfesionales, setDatosTipoProfesionales] = useState([]);
  const [datosFunciones, setDatosFunciones] = useState([]);
  const [checked, setChecked] = useState(role.activo);
  const [disabledTipoProfesional, setDisabledTipoProfesional] = useState(false);
  const [opendDlgError, setOpenDlgError] = useState(false);
  const [mensajeError, setMensajeError] = useState('');
  const [disabledGuardar, setDisabledGuardar] = useState(false);

  const [formValues, , , setFormValues] = useForm({
    Id: role.id,
    Descripcion: role.descripcion,
    IdFuncion: role.idFuncion,
    IdTipoProfesional: role.idTipoProfesional,
    Activo: role.activo
  });

  const apps = useRoleApps({ roleId: role.id, onError: setMensajeError });
  const notifications = useRoleNotifications({ roleId: role.id, onError: setMensajeError });
  const kpis = useRoleKpis({ roleId: role.id, onError: setMensajeError });
  const incompatibleRoles = useRoleIncompatibleRoles({ roleId: role.id, onError: setMensajeError });
  const consultas = useRoleConsultas({ roleId: role.id, onError: setMensajeError });

  useEffect(() => {
    setOpenDlgError(mensajeError !== '');
  }, [mensajeError]);

  const [getTipoProfesionales, { data: dataTipoProfesional, error: errorTipoProfs }] = useLazyQuery(GET_TIPO_PROFESIONALES, {
    fetchPolicy: 'cache-and-network'
  });

  const [getFunciones, { data: dataFunciones, error: errorFunciones }] = useLazyQuery(GET_FUNCIONES, {
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    getTipoProfesionales();
    getFunciones();
  }, []);

  useEffect(() => {
    if (!dataTipoProfesional?.tipoProfesionales?.success) return;
    setDatosTipoProfesionales(dataTipoProfesional.tipoProfesionales.valorEsperado || []);
  }, [dataTipoProfesional]);

  useEffect(() => {
    if (!dataFunciones?.funciones?.success) return;
    setDatosFunciones(dataFunciones.funciones.valorEsperado || []);
  }, [dataFunciones]);

  useEffect(() => {
    if (errorTipoProfs || errorFunciones) {
      setMensajeError('ERRORDB');
    }
  }, [errorTipoProfs, errorFunciones]);

  const [saveRole, { loading: loadingSave }] = useMutation(CREATE_ROL, {
    onCompleted: (response) => {
      if (response.saveRol.success) {
        setDisabledGuardar(false);
        toast.success('Se creó el rol correctamente');
        setTimeout(() => navigate('/role/'), 1000);
        return;
      }

      if (response.saveRol.error === 'ERRORDB' || parseInt(response.saveRol.error, 10) > 0) {
        setMensajeError(response.saveRol.error);
      } else {
        toast.error(response.saveRol.error);
      }
      setDisabledGuardar(false);
    },
    onError: () => {
      setMensajeError('ERRORDB');
      setDisabledGuardar(false);
      return false;
    }
  });

  const [updateRole, { loading: loadingUpdate }] = useMutation(UPDATE_ROL, {
    onCompleted: (response) => {
      if (response.updateRol.success) {
        setDisabledGuardar(false);
        toast.success('Se actualizó el rol correctamente');
        setTimeout(() => navigate('/role/'), 1000);
        return;
      }

      if (response.updateRol.error === 'ERRORDB' || parseInt(response.updateRol.error, 10) > 0) {
        setMensajeError(response.updateRol.error);
      } else {
        toast.error(response.updateRol.error);
      }
      setDisabledGuardar(false);
    },
    onError: () => {
      setMensajeError('ERRORDB');
      setDisabledGuardar(false);
      return false;
    }
  });

  useEffect(() => {
    setChildrenAction(() => (
      <div className='role-accion'>
        <CustomButton
          onClick={handleClickGuardar}
          isloading={loadingSave || loadingUpdate}
          disabled={disabledGuardar}
        >
          Guardar
        </CustomButton>
      </div>
    ));
  }, [
    setChildrenAction,
    formValues,
    apps.selectedRows,
    notifications.selectedRows,
    kpis.selectedRows,
    incompatibleRoles.selectedRows,
    consultas.selectedRows,
    disabledGuardar,
    loadingSave,
    loadingUpdate
  ]);

  const handleChangeActivo = (event) => {
    setChecked(event.target.checked);
    setFormValues({ ...formValues, Activo: event.target.checked });
  };

  const handleChangeDescripcion = (event) => {
    setFormValues({ ...formValues, Descripcion: event.target.value.toUpperCase() });
  };

  const handleChangeFuncion = (event) => {
    const idFuncion = event.target.value;
    const disableTipoProfesional = idFuncion === 1;
    setDisabledTipoProfesional(disableTipoProfesional);
    setFormValues({
      ...formValues,
      IdFuncion: idFuncion,
      IdTipoProfesional: disableTipoProfesional ? 0 : formValues.IdTipoProfesional
    });
  };

  const isFormValid = () => {
    if (!formValues.Descripcion?.length) {
      toast.error('Debe ingresar una descripción.');
      return false;
    }
    if (formValues.IdFuncion <= 0) {
      toast.error('Debe seleccionar una función.');
      return false;
    }
    if (formValues.IdFuncion > 1 && formValues.IdTipoProfesional <= 0) {
      toast.error('Debe seleccionar un tipo de profesional.');
      return false;
    }
    if (apps.selectedRows.length <= 0) {
      toast.error('Debe cargar al menos una pantalla.');
      return false;
    }
    if (notifications.selectedRows.length <= 0) {
      toast.error('Debe cargar al menos una notificacion.');
      return false;
    }
    return true;
  };

  const handleClickGuardar = () => {
    if (!isFormValid()) return;
    setDisabledGuardar(true);

    const stripTypename = (list) => list.map(({ __typename, ...rest }) => rest);
    if (role.id === 0) {
      saveRole({
        variables: {
          descripcion: formValues.Descripcion,
          idFuncion: formValues.IdFuncion,
          idTipoProfesional: formValues.IdTipoProfesional,
          listaPantallas: stripTypename(apps.selectedRows),
          listaNotificaciones: stripTypename(notifications.selectedRows),
          listaKpis: stripTypename(kpis.selectedRows),
          listaRolesNoCompatibles: stripTypename(incompatibleRoles.selectedRows),
          listaConsultaRol: adaptListaConsultaRolInput(stripTypename(consultas.selectedRows))
        }
      });
      return;
    }

    const listaKpisMapped = kpis.selectedRows.map((obj) => ({ id: 0, idKpi: obj.idKpi, idRol: role.id }));
    updateRole({
      variables: {
        id: role.id,
        descripcion: formValues.Descripcion,
        idFuncion: formValues.IdFuncion,
        idTipoProfesional: formValues.IdTipoProfesional,
        activo: checked,
        listaPantallas: stripTypename(apps.selectedRows),
        listaNotificaciones: stripTypename(notifications.selectedRows),
        listaKpis: stripTypename(listaKpisMapped),
        listaRolesNoCompatibles: stripTypename(incompatibleRoles.selectedRows),
        listaConsultaRol: adaptListaConsultaRolInput(stripTypename(consultas.selectedRows))
      }
    });
  };

  const handleCloseDlgError = () => setOpenDlgError(false);

  const handleAdd = (value) => {
    switch (value) {
      case 'Pantalla':
        return apps.openDialog();
      case 'Notificacion':
        return notifications.openDialog();
      case 'KPI':
        return kpis.openDialog();
      case 'Rol No Compatible':
        return incompatibleRoles.openDialog();
      case 'Consultas Generales':
        return consultas.openDialog();
      default:
        return null;
    }
  };

  const actions = [
    { icon: <ScreenSearchDesktopIcon />, name: 'Pantalla' },
    { icon: <NotificationsIcon />, name: 'Notificacion' },
    { icon: <LeaderboardIcon />, name: 'KPI' },
    { icon: <TaskIcon />, name: 'Rol No Compatible' },
    { icon: <HelpCenterIcon />, name: 'Consultas Generales' }
  ];

  return (
    <>
      <Grid container direction='row' spacing={2}>
        <Grid sx={{ boxSizing: 'border-box', width: '100%', paddingTop: '5%' }} size={{ xs: 6, md: 2.5, lg: 2.5 }}>
          <TextField
            style={{ backgroundColor: 'white', width: '100%' }}
            label='Descripcion'
            name='Descripcion'
            onChange={handleChangeDescripcion}
            size='small'
            type='text'
            value={formValues.Descripcion}
            variant='outlined'
          />
        </Grid>

        <Grid sx={{ boxSizing: 'border-box', width: '100%', paddingTop: '5%' }} size={{ xs: 6, md: 2.5, lg: 2.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-funcion'>Funcion</InputLabel>
            <Select labelId='select-funcion' value={formValues.IdFuncion} label='Funcion' onChange={handleChangeFuncion}>
              {datosFunciones.map((element) => (
                <MenuItem key={element.idFuncion} value={element.idFuncion}>{element.funcion}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid sx={{ boxSizing: 'border-box', width: '100%', paddingTop: '5%' }} size={{ xs: 6, md: 2.5, lg: 2.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-tipoProfesional'>Tipo Profesional</InputLabel>
            <Select
              labelId='select-tipoProfesional'
              value={formValues.IdTipoProfesional}
              label='Tipo Profesional'
              onChange={(e) => setFormValues({ ...formValues, IdTipoProfesional: e.target.value })}
              disabled={disabledTipoProfesional}
            >
              {datosTipoProfesionales.map((element) => (
                <MenuItem key={element.idTipoProfesional} value={element.idTipoProfesional}>{element.descripcion}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid sx={{ boxSizing: 'border-box', width: '100%', paddingTop: '5%' }} size={{ xs: 6, md: 2.5, lg: 2.5 }}>
          {edit && (
            <FormGroup>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography>Inactivo</Typography>
                <AntSwitch checked={checked} onChange={handleChangeActivo} inputProps={{ 'aria-label': 'activo' }} />
                <Typography>Activo</Typography>
              </Stack>
            </FormGroup>
          )}
        </Grid>
      </Grid>

      <FormControl variant='outlined' sx={{ alignItems: 'left', boxSizing: 'border-box', paddingTop: '3%' }} fullWidth>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Box sx={{ position: 'relative', mt: 2, height: 20 }}>
            <StyledSpeedDial ariaLabel='Role relations actions' style={{ top: '-20px', left: 'auto' }} icon={<SpeedDialIcon />} direction='right'>
              {actions.map((action) => (
                <SpeedDialAction
                  key={action.name}
                  icon={action.icon}
                  tooltipTitle={action.name}
                  onClick={() => handleAdd(action.name)}
                />
              ))}
            </StyledSpeedDial>
          </Box>
        </Stack>

        <RoleRelationSection title='Pantallas' columns={apps.selectedColumns} rows={apps.selectedRows} fileNameExport='Apps' />
        <RoleRelationSection title='Notificaciones' columns={notifications.selectedColumns} rows={notifications.selectedRows} fileNameExport='Notificaciones' />
        <RoleRelationSection title='KPI' columns={kpis.selectedColumns} rows={kpis.selectedRows} fileNameExport='KPI' />
        <RoleRelationSection title='Roles No Compatibles' columns={incompatibleRoles.selectedColumns} rows={incompatibleRoles.selectedRows} fileNameExport='Roles No Compatibles' />
        <RoleRelationSection title='Consultas Generales' columns={consultas.selectedColumns} rows={consultas.selectedRows} fileNameExport='Consultas Generales' />
      </FormControl>

      <ErrorDialog id='dlg-error-chgpass' open={opendDlgError} onClose={handleCloseDlgError} mensaje={mensajeError} />

      <RoleRelationDialog
        maxWidth='lg'
        open={apps.dialogOpen}
        title='Selecciona Apps'
        onCancel={apps.closeDialog}
        cancelText='Cerrar'
        onAccept={apps.confirmDialog}
        rows={apps.availableRows}
        columns={apps.availableColumns}
        fileNameExport='Apps'
      >
        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{ fontSize: '13pt' }}>
            <FormControl sx={{ backgroundColor: 'white' }} size='small'>
              <div style={{ display: 'flex', width: '100%' }}>
                <TextField
                  className='input-300'
                  label='Filtrar pantallas'
                  name='txtBuscarPantalla'
                  size='small'
                  type='text'
                  variant='outlined'
                  value={apps.searchValue}
                  onChange={(e) => apps.setSearchValue(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <>
                        <SearchIcon sx={{ cursor: 'pointer' }} />
                        <ClearIcon onClick={() => apps.setSearchValue('')} sx={{ cursor: 'pointer' }} />
                      </>
                    )
                  }}
                />
              </div>
            </FormControl>
          </div>
        </div>
      </RoleRelationDialog>

      <RoleRelationDialog
        maxWidth='xl'
        open={notifications.dialogOpen}
        title='Selecciona Notificaciones'
        onCancel={notifications.closeDialog}
        onAccept={notifications.confirmDialog}
        acceptText='Agregar'
        rows={notifications.availableRows}
        columns={notifications.availableColumns}
        fileNameExport='Notificaciones'
      />

      <RoleRelationDialog
        maxWidth='lg'
        open={kpis.dialogOpen}
        title='Selecciona KPI'
        onCancel={kpis.closeDialog}
        cancelText='Cerrar'
        onAccept={kpis.confirmDialog}
        acceptText='Agregar'
        rows={kpis.availableRows}
        columns={kpis.availableColumns}
        fileNameExport='KPI'
      />

      <RoleRelationDialog
        maxWidth='lg'
        open={incompatibleRoles.dialogOpen}
        title='Selecciona roles no compatibles'
        onCancel={incompatibleRoles.closeDialog}
        cancelText='Cerrar'
        onAccept={incompatibleRoles.confirmDialog}
        acceptText='Agregar'
        rows={incompatibleRoles.availableRows}
        columns={incompatibleRoles.availableColumns}
        fileNameExport='Roles'
      />

      <RoleRelationDialog
        maxWidth='lg'
        open={consultas.dialogOpen}
        title='Selecciona consultas'
        onCancel={consultas.closeDialog}
        cancelText='Cerrar'
        onAccept={consultas.confirmDialog}
        acceptText='Agregar'
        rows={consultas.availableRows}
        columns={consultas.availableColumns}
        fileNameExport='Consultas Generales'
      />
    </>
  );
}